import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { attemptChunkReloadOnce, clearChunkReloadFlag } from '@/lib/chunkReload';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

// A new deploy replaces every content-hashed asset filename. A browser tab
// left open from before the deploy still holds the OLD index.html/main
// bundle in memory, which references chunk filenames (e.g.
// ResultsVisualization-yQ4lGm1v.js) that no longer exist on the server —
// React.lazy()'s dynamic import() 404s. Crucially, React.lazy() caches the
// rejected import promise permanently once it fails, so re-rendering (the
// "Try Again" button below) throws the exact same cached rejection forever
// — it can never recover on its own. Only a full page reload re-fetches the
// current index.html with correct, up-to-date asset references.
// Message-pattern fallback for chunk-load failures that don't go through
// Vite's `vite:preloadError` window event (wired up in index.tsx as the
// primary, browser-independent signal for this same failure class) but
// still look like one by message text.
const CHUNK_LOAD_ERROR_PATTERNS = [
  /failed to fetch dynamically imported module/i,
  /loading chunk .* failed/i,
  /chunkloaderror/i,
  /importing a module script failed/i,
  /unable to preload css/i,
];

function isChunkLoadError(error: Error): boolean {
  return CHUNK_LOAD_ERROR_PATTERNS.some((p) => p.test(error.message));
}

/**
 * Error Boundary component to catch and handle React component errors
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidMount() {
    // Only clear on a genuinely error-free mount. When a child throws during
    // this boundary's OWN initial mount, React's lifecycle order is
    // getDerivedStateFromError -> commit -> componentDidMount ->
    // componentDidCatch — so if this ran unconditionally, it would wipe the
    // one-shot flag before componentDidCatch (below) ever gets to check it,
    // defeating the "don't auto-reload twice in a row" guard for errors that
    // surface during initial mount (the common case, since this boundary
    // wraps the whole app). Only a truly error-free mount means it's safe to
    // let a later, second deploy trigger its own fresh auto-reload attempt.
    if (!this.state.hasError) {
      clearChunkReloadFlag();
    }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    if (import.meta.env.DEV) {
      console.error('ErrorBoundary caught an error:', error, errorInfo);
    }

    if (isChunkLoadError(error) && attemptChunkReloadOnce()) {
      return;
    }
    // Either not a chunk-load error, or one was already attempted this
    // session and it's still failing — stop auto-reloading and fall through
    // to the normal error UI so the user isn't stuck in a silent refresh loop.

    this.setState({
      error,
      errorInfo,
    });

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // In production, you might want to send this to an error reporting service
    // Example: logErrorToService(error, errorInfo);
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <div className="mx-auto my-10 max-w-3xl rounded-lg border border-destructive bg-background p-10 shadow-sm">
          <h1 className="mt-0 flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-6 w-6" /> Something went wrong
          </h1>
          
          <p className="mb-5 text-base text-muted-foreground">
            The application encountered an unexpected error. Please try refreshing the page.
          </p>

          {this.state.error && (
            <details className="mb-5">
              <summary className="cursor-pointer rounded border border-border bg-muted p-2.5 font-bold">
                Error Details (for developers)
              </summary>
              <div className="mt-2.5 overflow-auto rounded border border-border bg-muted p-4 font-mono text-[13px]">
                <strong>Error:</strong>
                <pre className="my-2.5">{this.state.error.toString()}</pre>
                
                {this.state.errorInfo && (
                  <>
                    <strong>Component Stack:</strong>
                    <pre className="my-2.5 whitespace-pre-wrap">
                      {this.state.errorInfo.componentStack}
                    </pre>
                  </>
                )}
              </div>
            </details>
          )}

          <div className="flex gap-2.5">
            <Button onClick={this.handleReset}>Try Again</Button>
            <Button variant="secondary" onClick={() => window.location.reload()}>
              Reload Page
            </Button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
